import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Modal, Paper, Select, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import CloseOutlined from '@icons/CloseOutlined';
import { useFetchGroupsQuery, useInviteUserMutation } from '../../serviceAdmin';
import { InviteUserRequest } from '../../types';

export interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
}

const InviteUserModal = ({ open, onClose }: InviteUserModalProps) => {
  const [emailInput, setEmailInput] = useState<string>('');

  const [inviteUser] = useInviteUserMutation();

  // Search for users when typing, but only if we have at least 3 characters
  const { data: groups = [], isFetching } = useFetchGroupsQuery(undefined, { skip: !open });

  const formik = useFormik({
    initialValues: {
      email: '',
      group: '',
      role: ''
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.email) {
        errors.email = 'Email is required';
      } else if (values.email.length > 100) {
        errors.email = 'Email must be less than 100 characters';
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;
        if (!emailRegex.test(values.email)) {
          errors.email = 'Please enter a valid email address';
        }
      }
      return errors;
    },
    onSubmit: (values, { resetForm }) => {
      const request: InviteUserRequest = {
        email: values.email,
        group: values.group || undefined,
        role: values.role || undefined
      };

      inviteUser(request)
        .unwrap()
        .then((result) => {
          // Show combined success message with password on separate line if generated
          const toastContent = (
            <div>
              <Typography variant="body2">User {result.email} added successfully!</Typography>
              {result.password && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Password: {result.password}
                </Typography>
              )}
            </div>
          );

          toast.success(toastContent, {
            autoClose: false,
            hideProgressBar: true,
            closeButton: true
          });

          resetForm();
          setEmailInput('');
          onClose();
        })
        .catch((error) => {
          console.error('Error inviting user:', error);

          // if error is 409 them user already exists
          if (error.status === 409) {
            toast.error('User already exists; cannot send invitation.');
          } else {
            toast.error('Error sending invitation; please try again later.');
          }
        });
    }
  });

  const onEmailChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e?.target.value;
      setEmailInput(value || '');
      formik.handleChange(e); // Use formik's handleChange to trigger validation
    },
    [formik]
  );

  const onClearGroup = useCallback(() => {
    formik.setFieldValue('group', '');
    formik.setFieldValue('role', '');
  }, [formik]);

  const onClearRole = useCallback(() => {
    formik.setFieldValue('role', '');
  }, [formik]);

  const onCloseModal = useCallback(() => {
    formik.resetForm();
    setEmailInput('');
    onClose();
  }, [formik, onClose]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={onCloseModal} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={formik.handleSubmit}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            boxSizing: 'content-box',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            outline: 'none'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              alignSelf: 'stretch'
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              Invite by email
            </Typography>

            <IconButton size="small" onClick={onCloseModal}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 2
            }}
          >
            <TextField
              required
              fullWidth
              name="email"
              placeholder="Enter email address"
              label="Email"
              value={emailInput}
              onChange={onEmailChanged}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.email && formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <FormControl fullWidth sx={{ position: 'relative' }}>
              <InputLabel>Group</InputLabel>
              <Select
                value={formik.values.group}
                label="Group"
                disabled={isFetching}
                onChange={(e) => {
                  formik.setFieldValue('group', e.target.value);
                  // Clear role when group changes
                  if (!e.target.value) {
                    formik.setFieldValue('role', '');
                  }
                }}
                endAdornment={isFetching ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              >
                {isFetching ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading groups...</Typography>
                    </Box>
                  </MenuItem>
                ) : (
                  groups.map((group) => (
                    <MenuItem key={group.group_id} value={group.group_id}>
                      {group.group_name || group.group_id}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formik.values.group && (
                <IconButton
                  size="small"
                  onClick={onClearGroup}
                  sx={{
                    position: 'absolute',
                    right: 32,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                  }}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              )}
            </FormControl>

            <FormControl fullWidth disabled={!formik.values.group} sx={{ position: 'relative' }}>
              <InputLabel>Role</InputLabel>
              <Select value={formik.values.role} label="Role" onChange={(e) => formik.setFieldValue('role', e.target.value)}>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
              {formik.values.role && (
                <IconButton
                  size="small"
                  onClick={onClearRole}
                  sx={{
                    position: 'absolute',
                    right: 32,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                  }}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              )}
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <Button type="button" variant="outlined" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={!formik.values.email || !formik.isValid}>
              Invite
            </Button>
          </Box>
        </Paper>
      </form>
    </Modal>
  );
};

export default memo(InviteUserModal);
